import { useRef } from 'react';

interface SwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  minSwipeDistance?: number;
}

/**
 * A custom React hook to detect horizontal swipe gestures on an element.
 * 
 * @param options Configuration for swipe detection.
 * @param options.onSwipeLeft Callback for a left swipe.
 * @param options.onSwipeRight Callback for a right swipe.
 * @param options.minSwipeDistance The minimum horizontal distance to trigger a swipe. Defaults to 75.
 * @returns An object of touch event handlers to spread onto the target component.
 */
export const useSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  minSwipeDistance = 75,
}: SwipeOptions) => {
  const touchStartRef = useRef<number | null>(null);
  const touchEndRef = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    
    const distance = touchStartRef.current - touchEndRef.current;
    
    const isLeftSwipe = distance > minSwipeDistance;
    if (isLeftSwipe && onSwipeLeft) {
      onSwipeLeft();
    }
    
    const isRightSwipe = distance < -minSwipeDistance;
    if (isRightSwipe && onSwipeRight) {
      onSwipeRight();
    }
    
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };
};